import { Icon, notification } from 'antd';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { isSearchFiltersEmpty } from '@/src/compliance/utils';
import Button from '@/src/shared/components/Button';
import { space } from '@/styles';
import { convertMomentToDate } from '@/utils/dateTimeHelpers';
import { isAndroid } from '@/utils/detectBrowser';
import style from '../style';
import AddSavedSearch from './AddSavedSearch';

const { m } = space;

const defaultData = {
  searchId: null,
  searchDescription: '',
  includeOnHome: null,
  searchText: null,
  states: [],
  categories: [],
  charts: [],
  dateStart: null,
  dateEnd: null,
  page: null,
  pageSize: null,
  sortField: null,
  sortOrder: null,
  personId: null,
};

const normalizeCategory = category => ({
  categoryId: category.categoryId,
  category: category.category || category.categoryDescription,
});

const normalizeChart = chart => ({
  categories: (chart.categories || []).map(normalizeCategory),
  chartId: chart.chartId,
  chartTitle: chart.chartTitle,
  chartDescription: chart.chartDescription,
});

const buildSavedSearchData = ({ values, searchOptions, isRecentChanges }) => {
  const { categories = [], charts = [], states = [] } = searchOptions || {};

  const selectedCategory = values.categories
    ? categories.find(c => Number(c.categoryId) === Number(values.categories))
    : null;

  const selectedCharts = Array.isArray(values.charts)
    ? charts.filter(c => values.charts.map(Number).includes(Number(c.chartId)))
    : [];

  const selectedStates = Array.isArray(values.states)
    ? states.filter(s => values.states.includes(s.stateCode))
    : [];

  return {
    ...defaultData,
    recentChanges: isRecentChanges ? 'Y' : 'N',
    searchText: values.searchText || null,
    states: selectedStates,
    categories: selectedCategory ? [normalizeCategory(selectedCategory)] : [],
    charts: selectedCharts.map(normalizeChart),
    dateStart: Array.isArray(values.dateRanges)
      ? convertMomentToDate(values.dateRanges[0])
      : null,
    dateEnd: Array.isArray(values.dateRanges)
      ? convertMomentToDate(values.dateRanges[1])
      : null,
  };
};

const Buttons = ({ form, searchOptions, btnDisabled, handleClear, handleSaveSearch, isRecentChanges }) => {
  const [isModalVisible, setModalVisible] = useState(false);
  const [data, setData] = useState(defaultData);

  useEffect(() => {
    if (!isAndroid()) return;

    if (isModalVisible) {
      document.body.classList.add('is-fixed');
    } else {
      document.body.classList.remove('is-fixed');
    }
  }, [isModalVisible]);

  const handleSave = () => {
    if (isSearchFiltersEmpty(form)) {
      notification.error({
        message: 'Search',
        description: 'Search option is required.',
      });
      return;
    }

    form.validateFieldsAndScroll((err, values) => {
      if (err) return;

      setData(buildSavedSearchData({ values, searchOptions, isRecentChanges }));
      setModalVisible(true);
    });
  };

  return (
    <div css={style.footerContainer}>
      <AddSavedSearch
        visible={isModalVisible}
        data={data}
        closeModal={() => setModalVisible(false)}
        searchOptions={searchOptions}
        handleSaveSearch={handleSaveSearch}
        isRecentChanges={isRecentChanges}
      />
      <Button
        disabled={btnDisabled}
        type="button"
        size="small"
        css={[m.r16, m.b16]}
        htmlType="submit"
      >
        Search
      </Button>
      <Button
        disabled={btnDisabled}
        type="button"
        size="small"
        css={[m.r16, m.b16]}
        onClick={handleSave}
      >
        <Icon type="save" theme="filled" /> Save Search
      </Button>
      <Button size="small" theme="gray" css={m.b16} onClick={handleClear} disabled={btnDisabled}>
        <Icon type="sync" /> Reset All
      </Button>
    </div>
  );
};

Buttons.propTypes = {
  form: PropTypes.object.isRequired,
  searchOptions: PropTypes.object.isRequired,
  btnDisabled: PropTypes.bool,
  handleClear: PropTypes.func.isRequired,
  handleSaveSearch: PropTypes.func.isRequired,
};

Buttons.defaultProps = {
  btnDisabled: false,
};

export default Buttons;